import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Upload,
  Trash2,
  Copy,
  Check,
  X,
  Folder,
  FolderPlus,
  ArrowLeft,
  Move,
  Eye,
  ChevronRight,
  FileText,
  Pencil,
  Search,
  ArrowDownWideNarrow,
} from 'lucide-react';
import SelectField from './SelectField';
import {
  MEDIA_FILE_ACCEPT,
  mediaSrc as sharedMediaSrc,
  mediaAbsoluteUrl,
  isMediaImage,
  isMediaPdf,
  createMediaFolderPathResolver,
  MediaPickerButton,
} from './MediaLibrarySettings';

const MEDIA_TYPE_FILTERS = [
  { id: 'all', label: 'Alle' },
  { id: 'images', label: 'Bilder' },
  { id: 'pdf', label: 'PDF' },
  { id: 'documents', label: 'Dokumente' },
];

const matchesMediaType = (item, typeFilter) => {
  if (typeFilter === 'all') return true;
  if (typeFilter === 'images') return isMediaImage(item);
  if (typeFilter === 'pdf') return isMediaPdf(item);
  if (typeFilter === 'documents') return !isMediaImage(item) && !isMediaPdf(item);
  return true;
};

const mediaSortValue = (item) => {
  const created = item?.createdAt ?? item?.created_at ?? null;
  if (created != null) {
    const t = new Date(created).getTime();
    if (Number.isFinite(t)) return t;
  }
  const id = Number(item?.id);
  return Number.isFinite(id) ? id : 0;
};

const formatBytes = (n) => {
  const size = Number(n) || 0;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const fileExtLabel = (item) => {
  const fromName = String(item?.name || '').split('.').pop();
  if (fromName && fromName.length <= 5 && fromName !== item.name) return fromName.toUpperCase();
  const mime = String(item?.mime || '').split('/').pop();
  return (mime || 'FILE').toUpperCase().slice(0, 6);
};

/** Dateiname in Stamm + Endung (inkl. Punkt) zerlegen */
const splitFileName = (name = '') => {
  const raw = String(name || '');
  const lastDot = raw.lastIndexOf('.');
  if (lastDot <= 0 || lastDot === raw.length - 1) return { base: raw, ext: '' };
  const ext = raw.slice(lastDot);
  if (ext.length > 10 || !/^\.[a-z0-9]+$/i.test(ext)) return { base: raw, ext: '' };
  return { base: raw.slice(0, lastDot), ext };
};

/** Endung des Dateityps immer beibehalten, auch wenn der Nutzer sie im Feld entfernt */
const ensureFileExtension = (newName, originalName) => {
  const { ext } = splitFileName(originalName);
  let stem = String(newName || '').trim();
  if (!stem) return '';
  if (!ext) return stem;
  const { base, ext: typedExt } = splitFileName(stem);
  if (typedExt) stem = base;
  stem = stem.replace(/\.+$/g, '').trim() || 'datei';
  return `${stem}${ext}`;
};

const formatUploader = (item) => {
  const email = String(item?.uploadedBy || item?.uploaded_by || '').trim();
  if (!email || email === '__shared__') return '';
  return email;
};

const mediaSrc = sharedMediaSrc;

const MediaLibraryPage = () => {
  const [items, setItems] = useState([]);
  const [folders, setFolders] = useState([]);
  const [folderId, setFolderId] = useState(null); // null = root
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [preview, setPreview] = useState(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveTarget, setMoveTarget] = useState('root');
  const [moving, setMoving] = useState(false);
  const [dragOverTarget, setDragOverTarget] = useState(null); // null | 'root' | 'parent' | folderId
  const [draggingIds, setDraggingIds] = useState([]);
  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null); // { type: 'file'|'folder', id, name }
  const [renameValue, setRenameValue] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [leavingIds, setLeavingIds] = useState(() => new Set());
  const [copiedId, setCopiedId] = useState(null);
  const [publicBaseUrl, setPublicBaseUrl] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name-asc'); // name-asc | newest
  const [fileDropActive, setFileDropActive] = useState(false);
  const inputRef = useRef(null);
  const folderNameRef = useRef(null);
  const renameInputRef = useRef(null);
  const fileDragDepthRef = useRef(0);

  const folderQuery = folderId == null ? 'root' : String(folderId);

  const load = useCallback(async () => {
    try {
      const res = await axios.get('/api/media', { params: { folderId: folderQuery } });
      setItems(Array.isArray(res.data?.items) ? res.data.items : []);
      setFolders(Array.isArray(res.data?.folders) ? res.data.folders : []);
      setPublicBaseUrl(String(res.data?.publicBaseUrl || '').trim());
      setSelectedIds(new Set());
    } catch (err) {
      toast.error(err.response?.data?.error || 'Dateien konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [folderQuery]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  // Keep-alive: bei KI-Bildern und beim Zurückkehren zu Medien neu laden
  const location = useLocation();
  const mediaActive = location.pathname === '/media';
  const wasMediaActiveRef = useRef(mediaActive);
  useEffect(() => {
    const onMediaChanged = () => {
      load();
    };
    window.addEventListener('odium:media-changed', onMediaChanged);
    return () => window.removeEventListener('odium:media-changed', onMediaChanged);
  }, [load]);

  useEffect(() => {
    if (mediaActive && !wasMediaActiveRef.current) {
      load();
    }
    if (!mediaActive && wasMediaActiveRef.current) {
      // Portale hängen an document.body — Auswahl/Modals beim Verlassen schließen
      setSelectedIds(new Set());
      setMoveOpen(false);
      setPreview(null);
      setConfirmDelete(null);
      setFolderModalOpen(false);
      setRenameTarget(null);
    }
    wasMediaActiveRef.current = mediaActive;
  }, [mediaActive, load]);

  // PDF-Vorschau: als Blob laden (CRA-Proxy bricht Chrome-PDF-Viewer oft;
  // Fallback über PUBLIC_BASE_URL, wenn Datei nur auf dem Server liegt)
  useEffect(() => {
    if (!preview || !isMediaPdf(preview)) {
      setPdfBlobUrl('');
      setPdfLoading(false);
      setPdfError('');
      return undefined;
    }

    let cancelled = false;
    let objectUrl = '';
    setPdfLoading(true);
    setPdfError('');
    setPdfBlobUrl('');

    const candidates = [];
    const rel = mediaSrc(preview);
    if (rel) candidates.push(rel);
    const abs = mediaAbsoluteUrl(preview, publicBaseUrl);
    if (abs && abs !== rel) candidates.push(abs);

    (async () => {
      let lastErr = null;
      for (const src of candidates) {
        try {
          const crossOrigin =
            /^https?:\/\//i.test(src) &&
            typeof window !== 'undefined' &&
            !src.startsWith(window.location.origin);
          const res = await fetch(src, {
            credentials: crossOrigin ? 'omit' : 'same-origin',
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const buf = await res.arrayBuffer();
          if (!buf.byteLength) throw new Error('Leere Datei');
          const blob = new Blob([buf], { type: 'application/pdf' });
          objectUrl = URL.createObjectURL(blob);
          if (cancelled) {
            URL.revokeObjectURL(objectUrl);
            return;
          }
          setPdfBlobUrl(objectUrl);
          setPdfLoading(false);
          return;
        } catch (err) {
          lastErr = err;
        }
      }
      if (!cancelled) {
        setPdfLoading(false);
        setPdfError(lastErr?.message || 'PDF konnte nicht geladen werden');
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [preview, publicBaseUrl]);

  const folderById = useMemo(() => {
    const map = new Map();
    folders.forEach((f) => map.set(Number(f.id), f));
    return map;
  }, [folders]);

  const currentFolder = folderId == null ? null : folderById.get(Number(folderId)) || null;

  const childFolders = useMemo(() => {
    if (folderId == null) return folders.filter((f) => f.parentId == null);
    return folders.filter((f) => Number(f.parentId) === Number(folderId));
  }, [folders, folderId]);

  const breadcrumbs = useMemo(() => {
    const crumbs = [];
    let cur = currentFolder;
    const guard = new Set();
    while (cur && !guard.has(cur.id)) {
      crumbs.unshift(cur);
      guard.add(cur.id);
      cur = cur.parentId != null ? folderById.get(Number(cur.parentId)) : null;
    }
    return crumbs;
  }, [currentFolder, folderById]);

  const folderPathLabel = useMemo(() => createMediaFolderPathResolver(folders), [folders]);

  const visibleFolders = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list = childFolders;
    if (q) list = list.filter((f) => String(f.name || '').toLowerCase().includes(q));
    const sorted = [...list];
    if (sortBy === 'newest') {
      sorted.sort((a, b) => mediaSortValue(b) - mediaSortValue(a) || String(a.name || '').localeCompare(String(b.name || ''), 'de'));
    } else {
      sorted.sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'de'));
    }
    return sorted;
  }, [childFolders, searchQuery, sortBy]);

  const visibleItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list = items.filter((item) => matchesMediaType(item, typeFilter));
    if (q) list = list.filter((item) => String(item.name || '').toLowerCase().includes(q));
    const sorted = [...list];
    if (sortBy === 'newest') {
      sorted.sort((a, b) => mediaSortValue(b) - mediaSortValue(a) || String(a.name || '').localeCompare(String(b.name || ''), 'de'));
    } else {
      sorted.sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'de'));
    }
    return sorted;
  }, [items, searchQuery, typeFilter, sortBy]);

  const hasActiveFilters = Boolean(searchQuery.trim()) || typeFilter !== 'all';
  const empty = !loading && items.length === 0 && childFolders.length === 0;
  const emptyFiltered = !loading && !empty && visibleItems.length === 0 && visibleFolders.length === 0;

  const goBack = () => {
    if (!currentFolder) return;
    setFolderId(currentFolder.parentId == null ? null : currentFolder.parentId);
  };

  const uploadFiles = async (fileList, targetFolderId = undefined) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    const destFolderId = targetFolderId !== undefined ? targetFolderId : folderId;
    setUploading(true);
    let ok = 0;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        if (destFolderId != null) fd.append('folderId', String(destFolderId));
        const res = await axios.post('/api/media', fd);
        if (res.data?.item) {
          // Nur in die aktuelle Ansicht einfügen, wenn Ziel = aktueller Ordner
          const sameFolder =
            (destFolderId == null && folderId == null) ||
            Number(destFolderId) === Number(folderId);
          if (sameFolder) {
            setItems((prev) => [res.data.item, ...prev]);
          }
          ok += 1;
        }
      } catch (err) {
        toast.error(err.response?.data?.error || `„${file.name}“ fehlgeschlagen`);
      }
    }
    setUploading(false);
    if (ok) toast.success(ok === 1 ? 'Datei hochgeladen' : `${ok} Dateien hochgeladen`);
    if (inputRef.current) inputRef.current.value = '';
  };

  const openCreateFolder = () => {
    setFolderName('');
    setFolderModalOpen(true);
    requestAnimationFrame(() => folderNameRef.current?.focus());
  };

  const submitCreateFolder = async (e) => {
    e?.preventDefault?.();
    const name = folderName.trim();
    if (!name) {
      toast.error('Bitte einen Ordnernamen eingeben');
      return;
    }
    setCreatingFolder(true);
    try {
      const res = await axios.post('/api/media/folders', {
        name,
        parentId: folderId,
      });
      if (res.data?.folder) {
        setFolders((prev) => [...prev, res.data.folder].sort((a, b) => a.name.localeCompare(b.name, 'de')));
        toast.success('Ordner erstellt');
        setFolderModalOpen(false);
        setFolderName('');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ordner konnte nicht erstellt werden');
    } finally {
      setCreatingFolder(false);
    }
  };

  const requestDeleteFolder = (folder) => {
    if (!folder?.id) return;
    setConfirmDelete({ type: 'folder', folder });
  };

  const openRename = (type, entity) => {
    if (!entity?.id) return;
    const fullName = entity.name || '';
    if (type === 'file') {
      const { base, ext } = splitFileName(fullName);
      setRenameTarget({ type, id: entity.id, name: fullName, ext });
      setRenameValue(base);
    } else {
      setRenameTarget({ type, id: entity.id, name: fullName, ext: '' });
      setRenameValue(fullName);
    }
    requestAnimationFrame(() => {
      renameInputRef.current?.focus();
      renameInputRef.current?.select();
    });
  };

  const submitRename = async (e) => {
    e?.preventDefault?.();
    if (!renameTarget || renaming) return;
    const raw = renameValue.trim();
    if (!raw) {
      toast.error(renameTarget.type === 'folder' ? 'Bitte einen Ordnernamen eingeben' : 'Bitte einen Namen eingeben');
      return;
    }
    const name =
      renameTarget.type === 'file'
        ? ensureFileExtension(raw, renameTarget.name)
        : raw;
    if (!name) {
      toast.error('Bitte einen Namen eingeben');
      return;
    }
    if (name === renameTarget.name) {
      setRenameTarget(null);
      return;
    }
    setRenaming(true);
    try {
      if (renameTarget.type === 'folder') {
        const res = await axios.put(`/api/media/folders/${renameTarget.id}`, { name });
        const folder = res.data?.folder;
        if (folder) {
          setFolders((prev) =>
            prev
              .map((f) => (Number(f.id) === Number(folder.id) ? { ...f, ...folder } : f))
              .sort((a, b) => a.name.localeCompare(b.name, 'de'))
          );
          toast.success('Ordner umbenannt');
        }
      } else {
        const res = await axios.put(`/api/media/${renameTarget.id}`, { name });
        const item = res.data?.item;
        if (item) {
          setItems((prev) =>
            prev.map((x) => (Number(x.id) === Number(item.id) ? { ...x, ...item } : x))
          );
          if (preview && Number(preview.id) === Number(item.id)) {
            setPreview((p) => (p ? { ...p, ...item } : p));
          }
          toast.success('Umbenannt');
        }
      }
      setRenameTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Umbenennen fehlgeschlagen');
    } finally {
      setRenaming(false);
    }
  };

  const executeDeleteFolder = async (folder) => {
    await axios.delete(`/api/media/folders/${folder.id}`);
    const removeIds = new Set([Number(folder.id)]);
    let grew = true;
    while (grew) {
      grew = false;
      for (const f of folders) {
        const id = Number(f.id);
        const parent = f.parentId == null ? null : Number(f.parentId);
        if (removeIds.has(id) || parent == null || !removeIds.has(parent)) continue;
        removeIds.add(id);
        grew = true;
      }
    }
    setFolders((prev) => prev.filter((f) => !removeIds.has(Number(f.id))));
    if (folderId != null && removeIds.has(Number(folderId))) {
      setFolderId(folder.parentId == null ? null : folder.parentId);
    }
    toast.success('Ordner gelöscht');
    load();
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (visibleItems.length > 0 && selectedIds.size === visibleItems.length && visibleItems.every((i) => selectedIds.has(i.id))) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(visibleItems.map((i) => i.id)));
    }
  };

  const requestDeleteSelected = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    await openDeleteConfirm({ type: 'images', count: ids.length, ids });
  };

  const requestDeleteImage = async (item) => {
    if (!item?.id) return;
    await openDeleteConfirm({
      type: 'images',
      count: 1,
      ids: [item.id],
      name: item.name,
    });
  };

  const openDeleteConfirm = async (base) => {
    setConfirmDelete({
      ...base,
      usages: [],
      resolutions: {},
      usageLoading: true,
    });
    try {
      const res = await axios.post('/api/media/usage', { ids: base.ids });
      const usages = Array.isArray(res.data?.items) ? res.data.items : [];
      const resolutions = {};
      usages.forEach((u) => {
        if (u?.templates?.length) {
          resolutions[u.id] = { action: '', replaceAssetId: null, replaceName: '' };
        }
      });
      setConfirmDelete((prev) =>
        prev && prev.type === 'images' && JSON.stringify(prev.ids) === JSON.stringify(base.ids)
          ? { ...prev, usages, resolutions, usageLoading: false }
          : prev
      );
    } catch (err) {
      setConfirmDelete(null);
      toast.error(err.response?.data?.error || 'Nutzung konnte nicht geprüft werden');
    }
  };

  const setDeleteResolution = (assetId, patch) => {
    setConfirmDelete((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        resolutions: {
          ...prev.resolutions,
          [assetId]: {
            ...(prev.resolutions?.[assetId] || {}),
            ...patch,
          },
        },
      };
    });
  };

  const deleteUsageBlocked = useMemo(() => {
    if (!confirmDelete || confirmDelete.type !== 'images') return [];
    return (confirmDelete.usages || []).filter((u) => u?.templates?.length > 0);
  }, [confirmDelete]);

  const deleteResolutionsReady = useMemo(() => {
    if (!deleteUsageBlocked.length) return true;
    return deleteUsageBlocked.every((u) => {
      const r = confirmDelete?.resolutions?.[u.id];
      if (!r) return false;
      if (r.action === 'remove') return true;
      if (r.action === 'replace' && r.replaceAssetId) return true;
      return false;
    });
  }, [confirmDelete, deleteUsageBlocked]);

  const executeDeleteSelected = async (ids, resolve = {}) => {
    const idList = Array.isArray(ids) ? ids : [];
    if (!idList.length) return;

    setLeavingIds((prev) => {
      const next = new Set(prev);
      idList.forEach((id) => next.add(id));
      return next;
    });
    await new Promise((r) => setTimeout(r, 340));

    try {
      await axios.post('/api/media/bulk-delete', { ids: idList, resolve });
      setItems((prev) => prev.filter((x) => !idList.includes(x.id)));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        idList.forEach((id) => next.delete(id));
        return next;
      });
      if (preview && idList.includes(preview.id)) setPreview(null);
      toast.success(idList.length === 1 ? 'Datei gelöscht' : 'Dateien gelöscht');
      if (resolve && Object.keys(resolve).length > 0) {
        window.dispatchEvent(new CustomEvent('odium:templates-changed'));
      }
    } finally {
      setLeavingIds((prev) => {
        const next = new Set(prev);
        idList.forEach((id) => next.delete(id));
        return next;
      });
    }
  };

  const confirmDestructive = async () => {
    if (!confirmDelete || deleting) return;
    if (confirmDelete.type === 'images' && (confirmDelete.usageLoading || !deleteResolutionsReady)) {
      return;
    }
    setDeleting(true);
    const pending = confirmDelete;
    try {
      if (pending.type === 'folder') {
        await executeDeleteFolder(pending.folder);
        setConfirmDelete(null);
      } else if (pending.type === 'images') {
        const resolve = {};
        deleteUsageBlocked.forEach((u) => {
          const r = pending.resolutions?.[u.id];
          if (!r?.action) return;
          resolve[String(u.id)] = {
            action: r.action,
            ...(r.action === 'replace' ? { replaceAssetId: r.replaceAssetId } : {}),
          };
        });
        // Modal schließen, damit die Kacheln während der Leave-Animation sichtbar bleiben
        setConfirmDelete(null);
        await executeDeleteSelected(pending.ids, resolve);
      }
    } catch (err) {
      const data = err.response?.data;
      if (data?.code === 'MEDIA_IN_USE') {
        toast.error('Datei wird noch in Vorlagen verwendet');
        setConfirmDelete({
          ...pending,
          usages: Array.isArray(data.usages) ? data.usages : pending.usages,
          usageLoading: false,
        });
      } else {
        toast.error(data?.error || 'Löschen fehlgeschlagen');
      }
    } finally {
      setDeleting(false);
    }
  };

  const openMove = () => {
    if (!selectedIds.size) return;
    setMoveTarget('root');
    setMoveOpen(true);
  };

  const moveImagesToFolder = async (ids, targetFolderId) => {
    const list = (Array.isArray(ids) ? ids : [ids])
      .map((x) => Number(x))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (!list.length) return;

    const normalizedTarget =
      targetFolderId == null || targetFolderId === '' || targetFolderId === 'root'
        ? null
        : Number(targetFolderId);

    // Schon im Zielordner → nichts tun
    const current = folderId == null ? null : Number(folderId);
    if (normalizedTarget === current || (normalizedTarget == null && current == null)) {
      return;
    }

    setMoving(true);
    try {
      await axios.post('/api/media/move', {
        ids: list,
        folderId: normalizedTarget,
      });
      toast.success(list.length === 1 ? 'Datei verschoben' : `${list.length} Dateien verschoben`);
      setSelectedIds(new Set());
      setMoveOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Verschieben fehlgeschlagen');
    } finally {
      setMoving(false);
      setDragOverTarget(null);
      setDraggingIds([]);
    }
  };

  const confirmMove = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    await moveImagesToFolder(ids, moveTarget === 'root' ? null : Number(moveTarget));
  };

  const MEDIA_DND = 'application/x-media-ids';

  const onImageDragStart = (e, item) => {
    const ids =
      selectedIds.has(item.id) && selectedIds.size > 0
        ? [...selectedIds]
        : [item.id];
    setDraggingIds(ids);
    e.dataTransfer.setData(MEDIA_DND, JSON.stringify(ids));
    e.dataTransfer.setData('text/plain', String(item.id));
    e.dataTransfer.effectAllowed = 'move';
  };

  const onImageDragEnd = () => {
    setDraggingIds([]);
    setDragOverTarget(null);
  };

  const parseDragIds = (e) => {
    try {
      const raw = e.dataTransfer.getData(MEDIA_DND) || e.dataTransfer.getData('text/plain');
      if (!raw) return draggingIds.length ? draggingIds : [];
      if (raw.startsWith('[')) {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      }
      const n = Number(raw);
      return Number.isFinite(n) ? [n] : [];
    } catch {
      return draggingIds.length ? draggingIds : [];
    }
  };

  const isInternalMediaDrag = (e) =>
    Array.from(e.dataTransfer?.types || []).some(
      (t) => t === MEDIA_DND || t === 'text/plain' || t === 'application/json'
    ) && !Array.from(e.dataTransfer?.types || []).includes('Files');

  const onFolderDragOver = (e, targetKey) => {
    const osFiles = Array.from(e.dataTransfer?.types || []).includes('Files');
    if (!osFiles && !isInternalMediaDrag(e) && draggingIds.length === 0) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = osFiles ? 'copy' : 'move';
    setDragOverTarget(targetKey);
    if (osFiles) {
      fileDragDepthRef.current = 0;
      setFileDropActive(false);
    }
  };

  const onFolderDragLeave = (e, targetKey) => {
    if (dragOverTarget === targetKey) setDragOverTarget(null);
  };

  const onFolderDrop = async (e, targetFolderId, targetKey) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverTarget(null);
    fileDragDepthRef.current = 0;
    setFileDropActive(false);

    if (Array.from(e.dataTransfer?.types || []).includes('Files')) {
      const files = e.dataTransfer?.files;
      if (files?.length) await uploadFiles(files, targetFolderId);
      return;
    }

    const ids = parseDragIds(e);
    if (!ids.length) return;
    await moveImagesToFolder(ids, targetFolderId);
  };

  const hasOsFiles = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');

  const onOsDragEnter = (e) => {
    if (!hasOsFiles(e)) return;
    e.preventDefault();
    e.stopPropagation();
    fileDragDepthRef.current += 1;
    setFileDropActive(true);
  };

  const onOsDragOver = (e) => {
    if (!hasOsFiles(e)) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    if (!fileDropActive) setFileDropActive(true);
  };

  const onOsDragLeave = (e) => {
    if (!hasOsFiles(e) && fileDragDepthRef.current === 0) return;
    e.preventDefault();
    e.stopPropagation();
    fileDragDepthRef.current = Math.max(0, fileDragDepthRef.current - 1);
    if (fileDragDepthRef.current === 0) setFileDropActive(false);
  };

  const onOsDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    fileDragDepthRef.current = 0;
    setFileDropActive(false);
    if (!hasOsFiles(e)) return;
    if (draggingIds.length > 0) return;
    const files = e.dataTransfer?.files;
    if (files?.length) await uploadFiles(files);
  };

  const copyUrl = async (item) => {
    try {
      await navigator.clipboard.writeText(mediaAbsoluteUrl(item, publicBaseUrl));
      setCopiedId(item.id);
      toast.success('URL kopiert');
      window.setTimeout(() => setCopiedId(null), 1500);
    } catch (_) {
      toast.error('URL konnte nicht kopiert werden');
    }
  };

  const openItem = (item) => {
    if (isMediaImage(item) || isMediaPdf(item)) setPreview(item);
    else window.open(mediaSrc(item), '_blank', 'noopener,noreferrer');
  };

  const allVisibleSelected =
    visibleItems.length > 0 && visibleItems.every((i) => selectedIds.has(i.id));

  return (
    <div className="space-y-6 fade-in pb-8">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="page-title">Medien</h1>
          <p className="page-subtitle">
            Gemeinsame Bibliothek · {items.length} Datei{items.length === 1 ? '' : 'en'}
            {childFolders.length ? ` · ${childFolders.length} Ordner` : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" className="btn-secondary" onClick={openCreateFolder}>
            <FolderPlus className="w-4 h-4 mr-2" />
            Ordner
          </button>
          <label className={`btn-primary cursor-pointer ${uploading ? 'opacity-70 pointer-events-none' : ''}`}>
            <Upload className="w-4 h-4 mr-2" />
            {uploading ? 'Lädt…' : 'Hochladen'}
            <input
              ref={inputRef}
              type="file"
              accept={MEDIA_FILE_ACCEPT}
              multiple
              className="hidden"
              disabled={uploading}
              onChange={(e) => uploadFiles(e.target.files)}
            />
          </label>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[12rem] max-w-md">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400 w-4 h-4 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Datei oder Ordner suchen…"
            className="input-field pl-10"
            aria-label="Suche"
          />
        </div>
        <div className="flex items-center gap-1 p-1 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-neutral-100/80 dark:bg-neutral-800/80 overflow-x-auto">
          {MEDIA_TYPE_FILTERS.map((chip) => {
            const active = typeFilter === chip.id;
            return (
              <button
                key={chip.id}
                type="button"
                onClick={() => setTypeFilter(chip.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                  active
                    ? 'bg-white dark:bg-neutral-700 text-primary-700 dark:text-primary-300 shadow-sm'
                    : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-200'
                }`}
              >
                {chip.label}
              </button>
            );
          })}
        </div>
        <div className="relative min-w-[11rem]">
          <ArrowDownWideNarrow className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 w-4 h-4 pointer-events-none" />
          <SelectField
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="input-field pl-9"
            title="Sortierung"
          >
            <option value="name-asc">Name A–Z</option>
            <option value="newest">Neueste zuerst</option>
          </SelectField>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="card-header !py-3 border-b border-neutral-200 dark:border-neutral-700 bg-neutral-50/80 dark:bg-neutral-950/50 flex flex-wrap items-center gap-2">
          {folderId != null ? (
            <button
              type="button"
              onClick={goBack}
              onDragOver={(e) => onFolderDragOver(e, 'parent')}
              onDragLeave={(e) => onFolderDragLeave(e, 'parent')}
              onDrop={(e) =>
                onFolderDrop(
                  e,
                  currentFolder?.parentId == null ? null : currentFolder.parentId,
                  'parent'
                )
              }
              className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-xl text-sm font-medium transition-all ${
                dragOverTarget === 'parent'
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 hover:border-neutral-300 dark:hover:border-neutral-600'
              }`}
            >
              <ArrowLeft className="w-4 h-4" />
              Zurück
            </button>
          ) : null}

          <nav className="flex items-center gap-1 min-w-0 flex-1 overflow-x-auto" aria-label="Ordnerpfad">
            <button
              type="button"
              onClick={() => setFolderId(null)}
              onDragOver={(e) => onFolderDragOver(e, 'root')}
              onDragLeave={(e) => onFolderDragLeave(e, 'root')}
              onDrop={(e) => onFolderDrop(e, null, 'root')}
              className={`inline-flex items-center h-8 px-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                dragOverTarget === 'root'
                  ? 'bg-primary-600 text-white'
                  : folderId == null
                    ? 'bg-neutral-200/80 dark:bg-neutral-800 text-neutral-900 dark:text-white'
                    : 'text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800'
              }`}
            >
              Bibliothek
            </button>
            {breadcrumbs.map((crumb) => (
              <React.Fragment key={crumb.id}>
                <ChevronRight className="w-3.5 h-3.5 text-neutral-300 dark:text-neutral-600 flex-shrink-0" />
                <button
                  type="button"
                  onClick={() => setFolderId(crumb.id)}
                  className={`inline-flex items-center h-8 px-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                    Number(folderId) === Number(crumb.id)
                      ? 'bg-neutral-200/80 dark:bg-neutral-800 text-neutral-900 dark:text-white'
                      : 'text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800'
                  }`}
                >
                  {crumb.name}
                </button>
              </React.Fragment>
            ))}
          </nav>

          {visibleItems.length > 0 ? (
            <button
              type="button"
              onClick={toggleSelectAll}
              className="text-xs font-medium text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200 px-2 py-1.5"
            >
              {allVisibleSelected ? 'Nichts wählen' : 'Alles wählen'}
            </button>
          ) : null}
        </div>

        {mediaActive &&
          selectedIds.size > 0 &&
          createPortal(
            <div className="fixed inset-x-0 bottom-[max(0.75rem,env(safe-area-inset-bottom,0px))] sm:bottom-5 z-[90] flex justify-center px-3 pointer-events-none">
              <div className="pointer-events-auto inline-flex w-fit items-center gap-1.5 sm:gap-2 rounded-full border border-neutral-200 dark:border-neutral-700 bg-white/95 dark:bg-neutral-900/95 shadow-lg backdrop-blur-sm px-2 py-1.5 sm:px-3 sm:py-2 fade-in">
                <span className="pl-1.5 pr-0.5 text-xs sm:text-[13px] font-medium text-neutral-700 dark:text-neutral-200 tabular-nums whitespace-nowrap">
                  {selectedIds.size}
                  <span className="font-normal text-neutral-500 dark:text-neutral-400"> ausgewählt</span>
                </span>
                <span className="w-px h-4 bg-neutral-200 dark:bg-neutral-700 flex-shrink-0" />
                <div className="flex items-center gap-0.5 flex-shrink-0">
                  <button
                    type="button"
                    onClick={openMove}
                    className="inline-flex items-center justify-center gap-1 rounded-full min-h-9 min-w-9 sm:min-h-0 sm:min-w-0 sm:px-2.5 sm:py-1.5 text-xs font-medium text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 touch-manipulation"
                    title="Verschieben"
                  >
                    <Move className="w-4 h-4" />
                    <span className="hidden sm:inline">Verschieben</span>
                  </button>
                  <button
                    type="button"
                    onClick={requestDeleteSelected}
                    className="inline-flex items-center justify-center gap-1 rounded-full min-h-9 min-w-9 sm:min-h-0 sm:min-w-0 sm:px-2.5 sm:py-1.5 text-xs font-medium text-danger-600 dark:text-danger-400 hover:bg-danger-50 dark:hover:bg-danger-900/30 touch-manipulation"
                    title="Löschen"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span className="hidden sm:inline">Löschen</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedIds(new Set())}
                    className="inline-flex items-center justify-center rounded-full min-h-9 min-w-9 sm:min-h-0 sm:min-w-0 sm:p-1.5 text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 touch-manipulation"
                    title="Auswahl aufheben"
                    aria-label="Abbrechen"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}

        <div
          className={`relative p-4 sm:p-5 min-h-[28rem] sm:min-h-[32rem] transition-colors ${
            fileDropActive ? 'media-drop-active' : ''
          }`}
          onDragEnter={onOsDragEnter}
          onDragOver={onOsDragOver}
          onDragLeave={onOsDragLeave}
          onDrop={onOsDrop}
        >
          {fileDropActive ? (
            <div className="pointer-events-none absolute inset-3 z-20 flex items-center justify-center rounded-2xl border-2 border-dashed border-primary-500 bg-primary-50/85 dark:bg-primary-950/70">
              <div className="text-center px-4">
                <Upload className="w-7 h-7 mx-auto mb-2 text-primary-600 dark:text-primary-300" />
                <p className="text-sm font-semibold text-primary-800 dark:text-primary-200">
                  Dateien hier ablegen
                </p>
                <p className="text-xs text-primary-700/80 dark:text-primary-300/80 mt-1">
                  Upload in den aktuellen Ordner
                </p>
              </div>
            </div>
          ) : null}

          {loading ? (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-2.5">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
                <div key={i} className="rounded-xl skeleton aspect-square" />
              ))}
            </div>
          ) : empty ? (
            <label
              className={`flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed px-6 py-20 cursor-pointer transition-colors group ${
                fileDropActive
                  ? 'border-primary-500 bg-primary-50/50 dark:bg-primary-950/30'
                  : 'border-neutral-300 dark:border-neutral-600 bg-neutral-50/80 dark:bg-neutral-950/50 hover:border-primary-400 dark:hover:border-primary-500'
              }`}
            >
              <span className="w-12 h-12 rounded-2xl bg-primary-50 dark:bg-primary-950/50 text-primary-600 dark:text-primary-400 flex items-center justify-center group-hover:scale-105 transition-transform shadow-sm">
                <Upload className="w-5 h-5" />
              </span>
              <span className="text-base font-semibold text-neutral-800 dark:text-neutral-100">
                Dateien hierher oder per Klick hochladen
              </span>
              <span className="text-sm text-neutral-500 text-center max-w-sm">
                Bilder, PDF und Dokumente · max. 10&nbsp;MB · Bilder werden automatisch komprimiert
              </span>
              <input
                type="file"
                accept={MEDIA_FILE_ACCEPT}
                multiple
                className="hidden"
                disabled={uploading}
                onChange={(e) => uploadFiles(e.target.files)}
              />
            </label>
          ) : emptyFiltered ? (
            <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-neutral-300 dark:border-neutral-600 px-6 py-16 text-center">
              <p className="text-sm font-medium text-neutral-800 dark:text-neutral-100">Keine Treffer</p>
              <p className="text-sm text-neutral-500 max-w-sm">
                {hasActiveFilters
                  ? 'Passe Suche oder Filter an, um Dateien und Ordner in diesem Verzeichnis zu sehen.'
                  : 'In diesem Ordner ist nichts sichtbar.'}
              </p>
              {hasActiveFilters ? (
                <button
                  type="button"
                  className="btn-secondary text-sm mt-2"
                  onClick={() => {
                    setSearchQuery('');
                    setTypeFilter('all');
                  }}
                >
                  Filter zurücksetzen
                </button>
              ) : null}
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-2.5">
              {visibleFolders.map((f) => {
                const dropKey = `folder-${f.id}`;
                const isDrop = dragOverTarget === dropKey;
                return (
                  <div
                    key={dropKey}
                    className={`media-tile group relative rounded-xl border transition-all ${
                      isDrop
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-950/40 ring-2 ring-primary-500/30 scale-[1.02]'
                        : 'border-neutral-200 dark:border-neutral-700 bg-neutral-50/80 dark:bg-neutral-950/60 hover:border-neutral-300 dark:hover:border-neutral-600 hover:shadow-md'
                    }`}
                    onDragOver={(e) => onFolderDragOver(e, dropKey)}
                    onDragLeave={(e) => onFolderDragLeave(e, dropKey)}
                    onDrop={(e) => onFolderDrop(e, f.id, dropKey)}
                  >
                    <button
                      type="button"
                      onClick={() => setFolderId(f.id)}
                      className="w-full aspect-square flex flex-col items-center justify-center gap-2 p-3"
                      title={`Öffnen: ${f.name}`}
                    >
                      <span className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-950/50 text-amber-600 dark:text-amber-400 flex items-center justify-center shadow-sm">
                        <Folder className="w-5 h-5" />
                      </span>
                      <span className="text-xs font-semibold text-neutral-800 dark:text-neutral-100 truncate w-full text-center px-1">
                        {f.name}
                      </span>
                      <span className="text-[10px] text-neutral-400">Ordner</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => openRename('folder', f)}
                      className="absolute top-2 right-10 p-1.5 rounded-lg text-neutral-500 bg-white/90 dark:bg-neutral-900/90 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 hover:text-primary-600 shadow-sm transition-opacity"
                      title="Ordner umbenennen"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => requestDeleteFolder(f)}
                      className="absolute top-2 right-2 p-1.5 rounded-lg text-neutral-500 bg-white/90 dark:bg-neutral-900/90 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 hover:text-danger-600 shadow-sm transition-opacity"
                      title="Ordner löschen"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}

              {visibleItems.map((item) => {
                const selected = selectedIds.has(item.id);
                const src = mediaSrc(item);
                const isDragging = draggingIds.includes(item.id);
                const isLeaving = leavingIds.has(item.id);
                const image = isMediaImage(item);
                const pdf = isMediaPdf(item);
                return (
                  <div
                    key={item.id}
                    draggable={!isLeaving}
                    onDragStart={(e) => onImageDragStart(e, item)}
                    onDragEnd={onImageDragEnd}
                    className={`media-tile group relative rounded-xl border overflow-hidden transition-all cursor-grab active:cursor-grabbing ${
                      isLeaving ? 'media-tile-leave' : ''
                    } ${
                      isDragging ? 'opacity-40 scale-95' : ''
                    } ${
                      selected
                        ? 'border-primary-500 ring-2 ring-primary-500/30 shadow-md'
                        : 'border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600 hover:shadow-md'
                    }`}
                    title="Ziehen, um in einen Ordner zu legen"
                  >
                    <div className="relative aspect-square bg-neutral-100 dark:bg-neutral-950">
                      <button
                        type="button"
                        className="absolute inset-0 w-full h-full"
                        onClick={() => openItem(item)}
                      >
                        {image && src ? (
                          <img
                            src={src}
                            alt={item.name}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-gradient-to-br from-neutral-100 to-neutral-50 dark:from-neutral-900 dark:to-neutral-950">
                            <span className="w-10 h-10 rounded-xl bg-white dark:bg-neutral-800 shadow-sm flex items-center justify-center text-neutral-500">
                              <FileText className="w-5 h-5" />
                            </span>
                            <span className="text-[10px] font-bold tracking-wider text-neutral-400">
                              {fileExtLabel(item)}
                            </span>
                          </div>
                        )}
                      </button>

                      <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/60 via-black/25 to-transparent opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity pointer-events-none sm:group-hover:pointer-events-auto max-sm:pointer-events-auto">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            type="button"
                            onClick={() => openItem(item)}
                            className="p-1.5 rounded-lg bg-white/95 dark:bg-neutral-900/95 text-neutral-700 dark:text-neutral-200 hover:bg-white dark:hover:bg-neutral-800 shadow-sm"
                            title={image || pdf ? 'Vorschau' : 'Öffnen'}
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => openRename('file', item)}
                            className="p-1.5 rounded-lg bg-white/95 dark:bg-neutral-900/95 text-neutral-700 dark:text-neutral-200 hover:bg-white dark:hover:bg-neutral-800 shadow-sm"
                            title="Umbenennen"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => copyUrl(item)}
                            className="p-1.5 rounded-lg bg-white/95 dark:bg-neutral-900/95 text-neutral-700 dark:text-neutral-200 hover:bg-white dark:hover:bg-neutral-800 shadow-sm"
                            title="URL kopieren"
                          >
                            {copiedId === item.id ? (
                              <Check className="w-3.5 h-3.5 text-emerald-600" />
                            ) : (
                              <Copy className="w-3.5 h-3.5" />
                            )}
                          </button>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => toggleSelect(item.id)}
                        className="absolute top-2 left-2 z-10"
                        aria-label="Markieren"
                      >
                        <span
                          className={`flex h-5 w-5 items-center justify-center rounded-md border-2 shadow-sm transition-colors ${
                            selected
                              ? 'bg-primary-600 border-primary-600 text-white'
                              : 'bg-white/95 dark:bg-neutral-900/95 border-neutral-300 dark:border-neutral-500 text-transparent'
                          }`}
                        >
                          <Check className="w-3 h-3" />
                        </span>
                      </button>

                      <button
                        type="button"
                        onClick={() => requestDeleteImage(item)}
                        className="absolute top-2 right-2 z-10 p-1.5 rounded-lg text-neutral-500 bg-white/95 dark:bg-neutral-900/95 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 hover:text-danger-600 shadow-sm transition-opacity"
                        title="Löschen"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    <div className="px-2.5 py-2 bg-white dark:bg-neutral-900 border-t border-neutral-200 dark:border-neutral-700">
                      <p
                        className="text-xs font-medium text-neutral-800 dark:text-neutral-100 truncate"
                        title={item.name}
                      >
                        {item.name}
                      </p>
                      <p className="text-[10px] text-neutral-400 tabular-nums mt-0.5 truncate">
                        {formatBytes(item.size)}
                        {formatUploader(item) ? (
                          <span title={formatUploader(item)}> · {formatUploader(item)}</span>
                        ) : null}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {preview &&
        createPortal(
          <div className="fixed inset-0 z-[220] flex items-center justify-center p-4">
            <div className="modal-backdrop" onClick={() => setPreview(null)} />
            <div
              className={`modal-panel relative z-10 w-full flex flex-col scale-in ${
                isMediaPdf(preview) ? 'max-w-5xl h-[min(90vh,52rem)]' : 'max-w-3xl'
              }`}
            >
              <div className="px-5 py-4 border-b border-neutral-200 dark:border-neutral-700 flex items-start justify-between gap-3 shrink-0">
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-neutral-900 dark:text-white truncate">{preview.name}</h3>
                  <p className="text-xs text-neutral-500 mt-0.5">
                    {formatBytes(preview.size)} · {preview.mime}
                    {formatUploader(preview) ? (
                      <>
                        {' '}
                        · Hochgeladen von{' '}
                        <span className="text-neutral-700 dark:text-neutral-300">{formatUploader(preview)}</span>
                      </>
                    ) : null}
                  </p>
                </div>
                <button
                  type="button"
                  className="p-1.5 rounded-lg text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                  onClick={() => setPreview(null)}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div
                className={`flex-1 min-h-0 bg-neutral-50 dark:bg-neutral-950 ${
                  isMediaPdf(preview) ? '' : 'overflow-auto p-4 flex items-center justify-center'
                }`}
              >
                {isMediaPdf(preview) ? (
                  pdfLoading ? (
                    <div className="h-full flex items-center justify-center text-sm text-neutral-500">
                      PDF wird geladen…
                    </div>
                  ) : pdfError ? (
                    <div className="h-full flex flex-col items-center justify-center gap-3 px-6 text-center">
                      <p className="text-sm text-neutral-600 dark:text-neutral-300">
                        Vorschau fehlgeschlagen: {pdfError}
                      </p>
                      <button
                        type="button"
                        className="btn-secondary text-sm"
                        onClick={() =>
                          window.open(
                            mediaAbsoluteUrl(preview, publicBaseUrl) || mediaSrc(preview),
                            '_blank',
                            'noopener,noreferrer'
                          )
                        }
                      >
                        Auf dem Server öffnen
                      </button>
                    </div>
                  ) : pdfBlobUrl ? (
                    <iframe
                      title={preview.name || 'PDF-Vorschau'}
                      src={pdfBlobUrl}
                      className="w-full h-full border-0 bg-neutral-200 dark:bg-neutral-900"
                    />
                  ) : null
                ) : (
                  <img
                    src={mediaSrc(preview)}
                    alt={preview.name}
                    className="max-w-full max-h-[min(70vh,36rem)] object-contain rounded-lg"
                  />
                )}
              </div>
              <div className="px-5 py-3 border-t border-neutral-200 dark:border-neutral-700 flex flex-wrap gap-2 justify-between shrink-0">
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-950/30 rounded-lg px-3 py-2"
                    onClick={() => requestDeleteImage(preview)}
                  >
                    <Trash2 className="w-4 h-4" />
                    Löschen
                  </button>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg px-3 py-2"
                    onClick={() => openRename('file', preview)}
                  >
                    <Pencil className="w-4 h-4" />
                    Umbenennen
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="btn-secondary text-sm"
                    onClick={() =>
                      window.open(
                        pdfBlobUrl ||
                          mediaAbsoluteUrl(preview, publicBaseUrl) ||
                          mediaSrc(preview),
                        '_blank',
                        'noopener,noreferrer'
                      )
                    }
                  >
                    Neuem Tab öffnen
                  </button>
                  <button type="button" className="btn-secondary text-sm" onClick={() => copyUrl(preview)}>
                    URL kopieren
                  </button>
                  <button type="button" className="btn-primary text-sm" onClick={() => setPreview(null)}>
                    Schließen
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

      {moveOpen &&
        createPortal(
          <div className="fixed inset-0 z-[230] flex items-center justify-center p-4">
            <div className="modal-backdrop" onClick={() => !moving && setMoveOpen(false)} />
            <div className="modal-panel relative z-10 w-full max-w-sm scale-in">
              <div className="px-5 pt-4 pb-3 border-b border-neutral-200 dark:border-neutral-700">
                <h3 className="text-base font-semibold text-neutral-900 dark:text-white">Verschieben</h3>
                <p className="text-xs text-neutral-500 mt-0.5">
                  {selectedIds.size} Datei{selectedIds.size === 1 ? '' : 'en'} in Ordner verschieben
                </p>
              </div>
              <div className="px-5 py-4">
                <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1.5">
                  Zielordner
                </label>
                <SelectField
                  value={moveTarget}
                  onChange={(e) => setMoveTarget(e.target.value)}
                  className="input-field text-sm"
                >
                  <option value="root">Bibliothek</option>
                  {[...folders]
                    .sort((a, b) => folderPathLabel(a.id).localeCompare(folderPathLabel(b.id), 'de'))
                    .map((f) => (
                      <option key={f.id} value={String(f.id)}>
                        {folderPathLabel(f.id)}
                      </option>
                    ))}
                </SelectField>
              </div>
              <div className="px-5 py-3 border-t border-neutral-200 dark:border-neutral-700 flex flex-row-reverse gap-2">
                <button type="button" className="btn-primary text-sm" disabled={moving} onClick={confirmMove}>
                  {moving ? '…' : 'Verschieben'}
                </button>
                <button type="button" className="btn-secondary text-sm" disabled={moving} onClick={() => setMoveOpen(false)}>
                  Abbrechen
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {folderModalOpen &&
        createPortal(
          <div className="fixed inset-0 z-[230] flex items-center justify-center p-4">
            <div
              className="modal-backdrop"
              onClick={() => !creatingFolder && setFolderModalOpen(false)}
            />
            <form
              onSubmit={submitCreateFolder}
              className="modal-panel relative z-10 w-full max-w-sm scale-in"
            >
              <div className="px-5 pt-4 pb-3 border-b border-neutral-200 dark:border-neutral-700">
                <h3 className="text-base font-semibold text-neutral-900 dark:text-white">
                  {folderId == null ? 'Neuer Ordner' : 'Neuer Unterordner'}
                </h3>
                <p className="text-xs text-neutral-500 mt-0.5">
                  {folderId == null
                    ? 'Wird in der Bibliothek angelegt'
                    : `In „${currentFolder?.name || 'Ordner'}“`}
                </p>
              </div>
              <div className="px-5 py-4">
                <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1.5">
                  Ordnername
                </label>
                <input
                  ref={folderNameRef}
                  type="text"
                  value={folderName}
                  onChange={(e) => setFolderName(e.target.value)}
                  className="input-field text-sm"
                  placeholder="z.B. Logos"
                  maxLength={80}
                  disabled={creatingFolder}
                  autoFocus
                />
              </div>
              <div className="px-5 py-3 border-t border-neutral-200 dark:border-neutral-700 flex flex-row-reverse gap-2">
                <button type="submit" className="btn-primary text-sm" disabled={creatingFolder || !folderName.trim()}>
                  {creatingFolder ? '…' : 'Erstellen'}
                </button>
                <button
                  type="button"
                  className="btn-secondary text-sm"
                  disabled={creatingFolder}
                  onClick={() => setFolderModalOpen(false)}
                >
                  Abbrechen
                </button>
              </div>
            </form>
          </div>,
          document.body
        )}

      {renameTarget &&
        createPortal(
          <div className="fixed inset-0 z-[240] flex items-center justify-center p-4">
            <div
              className="modal-backdrop"
              onClick={() => !renaming && setRenameTarget(null)}
            />
            <form
              onSubmit={submitRename}
              className="modal-panel relative z-10 w-full max-w-sm scale-in"
            >
              <div className="px-5 pt-4 pb-3 border-b border-neutral-200 dark:border-neutral-700">
                <h3 className="text-base font-semibold text-neutral-900 dark:text-white">
                  {renameTarget.type === 'folder' ? 'Ordner umbenennen' : 'Datei umbenennen'}
                </h3>
                <p className="text-xs text-neutral-500 mt-0.5 truncate" title={renameTarget.name}>
                  Aktuell: {renameTarget.name || '—'}
                </p>
              </div>
              <div className="px-5 py-4">
                <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1.5">
                  Neuer Name
                </label>
                {renameTarget.type === 'file' && renameTarget.ext ? (
                  <div className="flex items-stretch gap-0">
                    <input
                      ref={renameInputRef}
                      type="text"
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      className="input-field text-sm rounded-r-none flex-1 min-w-0"
                      maxLength={120}
                      disabled={renaming}
                      autoFocus
                      aria-label="Dateiname ohne Endung"
                    />
                    <span
                      className="inline-flex items-center px-3 text-sm font-medium text-neutral-500 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-800 border border-l-0 border-neutral-200 dark:border-neutral-700 rounded-r-xl select-none"
                      title="Dateityp-Endung bleibt erhalten"
                    >
                      {renameTarget.ext}
                    </span>
                  </div>
                ) : (
                  <input
                    ref={renameInputRef}
                    type="text"
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    className="input-field text-sm"
                    maxLength={renameTarget.type === 'folder' ? 80 : 120}
                    disabled={renaming}
                    autoFocus
                  />
                )}
              </div>
              <div className="px-5 py-3 border-t border-neutral-200 dark:border-neutral-700 flex flex-row-reverse gap-2">
                <button type="submit" className="btn-primary text-sm" disabled={renaming || !renameValue.trim()}>
                  {renaming ? '…' : 'Speichern'}
                </button>
                <button
                  type="button"
                  className="btn-secondary text-sm"
                  disabled={renaming}
                  onClick={() => setRenameTarget(null)}
                >
                  Abbrechen
                </button>
              </div>
            </form>
          </div>,
          document.body
        )}

      {confirmDelete &&
        createPortal(
          <div className="fixed inset-0 z-[230] flex items-center justify-center p-4">
            <div
              className="modal-backdrop"
              onClick={() => !deleting && setConfirmDelete(null)}
            />
            <div className="modal-panel relative z-10 w-full max-w-lg scale-in max-h-[90vh] flex flex-col overflow-hidden">
              <div className="px-5 pt-4 pb-3 border-b border-neutral-200 dark:border-neutral-700 flex-shrink-0">
                <h3 className="text-base font-semibold text-neutral-900 dark:text-white">
                  {confirmDelete.type === 'folder' ? 'Ordner löschen?' : 'Dateien löschen?'}
                </h3>
                <p className="text-xs text-neutral-500 mt-1 leading-relaxed">
                  {confirmDelete.type === 'folder' ? (
                    <>
                      Ordner „{confirmDelete.folder?.name}“ wird gelöscht. Der Ordner muss leer sein (keine Dateien).
                      Leere Unterordner werden mitgelöscht.
                    </>
                  ) : confirmDelete.count === 1 ? (
                    <>
                      Datei{confirmDelete.name ? ` „${confirmDelete.name}“` : ''} unwiderruflich löschen?
                    </>
                  ) : (
                    <>
                      {confirmDelete.count} Dateien unwiderruflich löschen?
                    </>
                  )}
                </p>
              </div>

              {confirmDelete.type === 'images' ? (
                <div className="flex-1 min-h-0 overflow-y-auto px-5 py-3 space-y-3">
                  {confirmDelete.usageLoading ? (
                    <p className="text-xs text-neutral-500">Prüfe Verwendung in Vorlagen…</p>
                  ) : deleteUsageBlocked.length === 0 ? (
                    <p className="text-xs text-neutral-500">
                      Keine Verwendung in Vorlagen gefunden.
                    </p>
                  ) : (
                    <>
                      <p className="text-xs text-amber-700 dark:text-amber-300 leading-relaxed">
                        {deleteUsageBlocked.length === 1
                          ? 'Diese Datei wird in Vorlagen verwendet. Bitte Ersatz wählen oder die Datei aus den Vorlagen entfernen.'
                          : `${deleteUsageBlocked.length} Dateien werden in Vorlagen verwendet. Bitte je Datei Ersatz wählen oder entfernen.`}
                      </p>
                      {deleteUsageBlocked.map((u) => {
                        const resolution = confirmDelete.resolutions?.[u.id] || {};
                        return (
                          <div
                            key={u.id}
                            className="rounded-xl border border-neutral-200 dark:border-neutral-700 p-3 space-y-2.5"
                          >
                            <div>
                              <p className="text-sm font-medium text-neutral-900 dark:text-white truncate">
                                {u.name || `Datei #${u.id}`}
                              </p>
                              <p className="text-[11px] text-neutral-500 mt-0.5">
                                In {u.templates.length} Vorlage{u.templates.length === 1 ? '' : 'n'}:{' '}
                                {u.templates
                                  .map((t) => {
                                    const reasons = Array.isArray(t.reasons) && t.reasons.length
                                      ? ` (${t.reasons.join(', ')})`
                                      : '';
                                    return `${t.name}${reasons}`;
                                  })
                                  .join(', ')}
                              </p>
                            </div>
                            <div className="space-y-1.5">
                              <label className="flex items-start gap-2 text-xs text-neutral-700 dark:text-neutral-300 cursor-pointer">
                                <input
                                  type="radio"
                                  name={`media-del-${u.id}`}
                                  className="mt-0.5"
                                  checked={resolution.action === 'replace'}
                                  onChange={() =>
                                    setDeleteResolution(u.id, {
                                      action: 'replace',
                                      replaceAssetId: resolution.replaceAssetId || null,
                                      replaceName: resolution.replaceName || '',
                                    })
                                  }
                                />
                                <span className="min-w-0 flex-1">
                                  <span className="font-medium">Durch andere Datei ersetzen</span>
                                  <span className="block text-neutral-500 mt-1">
                                    {resolution.action === 'replace' ? (
                                      <span className="flex flex-wrap items-center gap-2">
                                        <MediaPickerButton
                                          label={
                                            resolution.replaceName
                                              ? 'Andere Datei wählen'
                                              : 'Ersatzdatei wählen'
                                          }
                                          excludeIds={confirmDelete.ids}
                                          onSelectItem={(item) =>
                                            setDeleteResolution(u.id, {
                                              action: 'replace',
                                              replaceAssetId: item.id,
                                              replaceName: item.name,
                                            })
                                          }
                                        />
                                        {resolution.replaceName ? (
                                          <span className="text-neutral-700 dark:text-neutral-200 truncate max-w-[12rem]">
                                            {resolution.replaceName}
                                          </span>
                                        ) : null}
                                      </span>
                                    ) : (
                                      'In allen Vorlagen durch eine andere Mediathek-Datei ersetzen'
                                    )}
                                  </span>
                                </span>
                              </label>
                              <label className="flex items-start gap-2 text-xs text-neutral-700 dark:text-neutral-300 cursor-pointer">
                                <input
                                  type="radio"
                                  name={`media-del-${u.id}`}
                                  className="mt-0.5"
                                  checked={resolution.action === 'remove'}
                                  onChange={() =>
                                    setDeleteResolution(u.id, {
                                      action: 'remove',
                                      replaceAssetId: null,
                                      replaceName: '',
                                    })
                                  }
                                />
                                <span>
                                  <span className="font-medium">Aus Vorlagen entfernen</span>
                                  <span className="block text-neutral-500">
                                    Bild-URLs leeren und verknüpfte Anhänge aus den Vorlagen entfernen
                                  </span>
                                </span>
                              </label>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
              ) : null}

              <div className="px-5 py-3 border-t border-neutral-200 dark:border-neutral-700 flex flex-row-reverse gap-2 bg-neutral-50/80 dark:bg-neutral-950/40 flex-shrink-0">
                <button
                  type="button"
                  className="btn-danger text-sm"
                  disabled={
                    deleting ||
                    (confirmDelete.type === 'images' &&
                      (confirmDelete.usageLoading || !deleteResolutionsReady))
                  }
                  onClick={confirmDestructive}
                >
                  {deleting ? '…' : 'Löschen'}
                </button>
                <button
                  type="button"
                  className="btn-secondary text-sm"
                  disabled={deleting}
                  onClick={() => setConfirmDelete(null)}
                >
                  Abbrechen
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default MediaLibraryPage;
